import React, { useState, useEffect } from "react";
import { auth, db, storage } from "./firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, collection, getDocs, runTransaction, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useTable, useSortBy, usePagination } from "react-table";
import './css/ManageUsers.css';
import { deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const counterRef = doc(db, "users", "RegisteredUsers");

const ManageUsers = () => {
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    location: "",
    country: "",
    dateOfBirth: "",
    phoneNumber: "",
    userGender: "",
    genderPreference: "",
    subscriptionPlan: "",
    lookingFor: "",
    height: "",
    status: "",
    onlineStatus: false,
    notificationStatus: false,
    aboutMe: "",
    motherTongue: "",
    religion: "",
    community: "",
    degree: "",
    dayJob: "",
    growUpCountry: "",
    originCountry: "",
    settledCountry: "",
    bodyBuild: "",
    profileImageUrls: [],
    fcmToken: "",
    profileDiscovery: false,
    referrerUserId: "",
    referrals: [],
    currentDiscountPercentage: 0,
    blockedUsers: [],
    isFirstTimeSubscription: false,
    currentPosition: {
      address: "",
      city: "",
      latitude: "",
      longitude: "",
      countryCode: ""
    },
    password: "",
    confirmPassword: "",
    selectedLikesInvolvesMap: {
      books: [],
      childrenView: [],
      foods: [],
      hobbies: [],
      interests: [],
      movies: [],
      music: [],
      relaxWay: [],
      sleepingHabit: [],
      sports: [],
      tvShows: [],
      vacations: [],
    },
    selectedPersonalityTraitsMap: {
      drink: "",
      eveningRoutine: [],
      exercise: "",
      passions: [],
      personalityType: "",
      pets: [],
      poison: [],
      smoke: "",
      starSign: "",
      tripsType: [],
      weatherType: [],
      weekendActivities: [],
      weekendNight: []
    }
  });

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [users, setUsers] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [searchNameTerm, setSearchNameTerm] = useState("");
  const [searchIdTerm, setSearchIdTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [searchPlanTerm, setSearchPlanTerm] = useState("");
  const [searchDateTerm, setSearchDateTerm] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImageIndex(null);
  };

  const handlePlanChange = (e) => {
    setSearchPlanTerm(e.target.value);
  };

  const checkUserRole = async () => {
    try {
      setIsCheckingRole(true);
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.log("No authenticated user found");
        setUserRole(null);
        return;
      }

      const adminUsersRef = collection(db, "adminUsers");
      const q = query(adminUsersRef, where("email", "==", currentUser.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        setUserRole(userData.role);
        console.log("User role:", userData.role);
      } else {
        console.log("User not found in adminUsers collection");
        setUserRole(null);
      }
    } catch (error) {
      console.error("Error checking user role:", error);
      setUserRole(null);
    } finally {
      setIsCheckingRole(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkUserRole();
      } else {
        setUserRole(null);
        setIsCheckingRole(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        const userList = userSnapshot.docs
          .filter((doc) => doc.id !== 'RegisteredUsers')
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        setUsers(userList);
        setFilteredUsers(userList);
      } catch (error) {
        console.error("Error fetching users: ", error);
        showError("Failed to fetch users");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  //Check and update profile discovery when reload the page
  useEffect(() => {
    const checkAndUpdateProfileDiscovery = async () => {
      try {
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        
        const batch = [];
        
        for (const docSnapshot of userSnapshot.docs) {
          if (docSnapshot.id === 'RegisteredUsers') continue;
          
          const userData = docSnapshot.data();
          const profileImageUrls = userData.profileImageUrls || [];
          const profileDiscovery = userData.profileDiscovery;
          
          //Condition: profileImageUrls null/empty/less than 3 AND profileDiscovery is true
          if (profileImageUrls.length < 3 && profileDiscovery === true) {
            batch.push({
              id: docSnapshot.id,
              needsUpdate: true
            });
          }
        }
        
        // Update all users that need profileDiscovery set to false
        for (const user of batch) {
          const userRef = doc(db, "users", user.id);
          await setDoc(userRef, { profileDiscovery: false }, { merge: true });
          console.log(`Updated profileDiscovery to false for user: ${user.id}`);
        }
        
        if (batch.length > 0) {
          console.log(`Updated ${batch.length} users with profileDiscovery = false`);
          // Refresh the user list after updates
          const updatedSnapshot = await getDocs(usersCollection);
          const userList = updatedSnapshot.docs
            .filter((doc) => doc.id !== 'RegisteredUsers')
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
          setUsers(userList);
          setFilteredUsers(userList);
        }
        
      } catch (error) {
        console.error("Error checking and updating profileDiscovery:", error);
      }
    };
    
    if (users.length > 0) {
      checkAndUpdateProfileDiscovery();
    }
  }, [users.length]);

  //Check and update subscription expired when reload the page
  useEffect(() => {
    const checkAndUpdateExpiredSubscriptions = async () => {
      try {
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
        
        const batch = [];
        
        for (const docSnapshot of userSnapshot.docs) {
          if (docSnapshot.id === 'RegisteredUsers') continue;
          
          const userData = docSnapshot.data();
          const subscriptions = userData.subscriptions;
          
          if (subscriptions && subscriptions.validUntil) {
            let validUntilDate = subscriptions.validUntil;
            
            // Handle Firestore timestamp
            if (validUntilDate.seconds) {
              validUntilDate = new Date(validUntilDate.seconds * 1000);
            } else {
              validUntilDate = new Date(validUntilDate);
            }
            
            validUntilDate.setHours(0, 0, 0, 0);
            
            // Check if subscription is expired
            if (validUntilDate < today) {
              batch.push({
                id: docSnapshot.id,
                needsUpdate: true
              });
            }
          }
        }
        
        // Update all users with expired subscriptions
        for (const user of batch) {
          const userRef = doc(db, "users", user.id);
          await setDoc(userRef, { 
            subscriptions: {
              availedBoost: 0,
              boostedUntil: null,
              planName: "Free",
              validUntil: ""
            }
          }, { merge: true });
          console.log(`Updated expired subscription to Free for user: ${user.id}`);
        }
        
        if (batch.length > 0) {
          console.log(`Updated ${batch.length} users with expired subscriptions`);
          // Refresh the user list after updates
          const updatedSnapshot = await getDocs(usersCollection);
          const userList = updatedSnapshot.docs
            .filter((doc) => doc.id !== 'RegisteredUsers')
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
          setUsers(userList);
          setFilteredUsers(userList);
        }
        
      } catch (error) {
        console.error("Error checking and updating expired subscriptions:", error);
      }
    };
    
    if (users.length > 0) {
      checkAndUpdateExpiredSubscriptions();
    }
  }, [users.length]);
  useEffect(() => {
    let filtered = users;

    if (searchNameTerm !== "") {
      filtered = filtered.filter(user =>
        user.name && user.name.toLowerCase().includes(searchNameTerm.toLowerCase())
      );
    }

    if (searchIdTerm !== "") {
      filtered = filtered.filter(user =>
        (user.id && user.id.toLowerCase().includes(searchIdTerm.toLowerCase())) ||
        (user.userId && user.userId.toLowerCase().includes(searchIdTerm.toLowerCase()))
      );
    }

    if (searchPlanTerm !== "") {
      filtered = filtered.filter(user => user.subscriptions?.planName === searchPlanTerm);
    }

    if (searchDateTerm !== "") {
      filtered = filtered.filter(user => {
        if (!user.doj) return false;
        let dojDate = user.doj;
        if (dojDate.seconds) {
          dojDate = new Date(dojDate.seconds * 1000);
        } else {
          dojDate = new Date(dojDate);
        }
        const formattedDoj = dojDate.toISOString().split("T")[0];
        return formattedDoj === searchDateTerm;
      });
    }
    setFilteredUsers(filtered);
  }, [searchNameTerm, searchIdTerm, searchDateTerm, searchPlanTerm, users]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 5000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 5000);
  };

  const handleNameSearchChange = (e) => {
    setSearchNameTerm(e.target.value);
  };

  const handleIdSearchChange = (e) => {
    setSearchIdTerm(e.target.value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const formatDateForStorage = (date) => {
    const dateObj = new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(dateObj);
  };

  const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

const handleImageChange = (e) => {
  const files = Array.from(e.target.files);

  const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
  if (invalidFiles.length > 0) {
    alert("Only JPG and PNG files are allowed!");
    return;
  }

  const newFiles = [...imageFiles, ...files];
  setImageFiles(newFiles);

  const previews = newFiles.map(file => URL.createObjectURL(file));
  setUserDetails(prevDetails => ({
    ...prevDetails,
    imagePreviews: previews,
  }));
};


  const handleRemoveImage = (index) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImageFiles(newFiles);

    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      imagePreviews: newPreviews,
    }));
  };

  const uploadImages = async (newUserId) => {
    const uploadedImageUrls = [];

    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      const storageRef = ref(storage, `profile-images/${newUserId}_${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      uploadedImageUrls.push(imageUrl);
    }

    return uploadedImageUrls;
  };

  const isEmailExists = async (email) => {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking email existence:", error);
      throw error;
    }
  };

  const isPhoneExists = async (phoneNumber) => {
    if (!phoneNumber) return false;

    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("phoneNumber", "==", phoneNumber));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking phone existence:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (userDetails.password !== userDetails.confirmPassword) {
      showError("Passwords do not match!");
      return;
    }

    if (imageFiles.length < 3 || imageFiles.length > 6) {
      showError("Please select between 3 and 6 images.");
      return;
    }

    try {
      setIsLoading(true);

      const emailExists = await isEmailExists(userDetails.email);
      if (emailExists) {
        showError("Email already exists. Please use a different email.");
        setIsLoading(false);
        return;
      }

      if (userDetails.phoneNumber) {
        const phoneExists = await isPhoneExists(userDetails.phoneNumber);
        if (phoneExists) {
          showError("Phone number already exists. Please use a different number.");
          setIsLoading(false);
          return;
        }
      }

      const formattedDate = formatDateForStorage(userDetails.dateOfBirth);
      let newUserId = "";
      await runTransaction(db, async (transaction) => {
        const counterSnap = await transaction.get(counterRef);
        if (!counterSnap.exists()) {
          throw new Error("RegisteredUsers counter document does not exist.");
        }

        const currentCount = counterSnap.data().totalCount || 0;
        const nextCount = currentCount + 1;
        newUserId = `YBE${nextCount.toString().padStart(8, "0")}`;
        transaction.update(counterRef, { totalCount: nextCount });
      });

      const uploadedImageUrls = await uploadImages(newUserId);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userDetails.email,
        userDetails.password,
      );

      const todayDate = new Date().toISOString().split("T")[0];
      const subscriptions = {
        planName: userDetails.subscriptionPlan || "Free",
        availedBoost: userDetails.availedBoost || 0,
        boostedUntil: userDetails.boostedUntil || null,
        validUntil: userDetails.validUntil || null,
      };
      const dailyLimit = {
        swipeCount: 2,
        profileViewCount: 5,
        date: todayDate
      };

      await setDoc(doc(db, "users", newUserId), {
        name: userDetails.name,
        email: userDetails.email,
        location: userDetails.location,
        country: userDetails.country || "", // Add this line with fallback
        dateOfBirth: formattedDate,
        phoneNumber: userDetails.phoneNumber,
        userGender: userDetails.userGender,
        genderPreference: userDetails.genderPreference,
        lookingFor: userDetails.lookingFor,
        height: userDetails.height,
        status: userDetails.status,
        onlineStatus: userDetails.onlineStatus,
        notificationStatus: userDetails.notificationStatus,
        aboutMe: userDetails.aboutMe,
        motherTongue: userDetails.motherTongue,
        religion: userDetails.religion,
        community: userDetails.community,
        degree: userDetails.degree,
        dayJob: userDetails.dayJob,
        growUpCountry: userDetails.growUpCountry,
        originCountry: userDetails.originCountry,
        settledCountry: userDetails.settledCountry,
        bodyBuild: userDetails.bodyBuild,
        profileImageUrls: uploadedImageUrls,
        isFirstTimeSubscription: userDetails.isFirstTimeSubscription,
        fcmToken: userDetails.fcmToken,
        profileDiscovery: userDetails.profileDiscovery,
        referrerUserId: userDetails.referrerUserId,
        referrals: userDetails.referrals,
        currentDiscountPercentage: userDetails.currentDiscountPercentage,
        blockedUsers: userDetails.blockedUsers,
        currentPosition: userDetails.currentPosition,
        selectedLikesInvolvesMap: userDetails.selectedLikesInvolvesMap,
        selectedPersonalityTraitsMap: userDetails.selectedPersonalityTraitsMap,
        createdAt: new Date(),
        userId: newUserId,
        isDisabled: true,
        doj: todayDate,
        subscriptions: subscriptions,
        dailyLimit: dailyLimit
      });

      showSuccess("User added successfully!");
      setIsFormVisible(false);

      // Reset form
      setUserDetails({
        name: "",
        email: "",
        location: "",
        country: "",
        dateOfBirth: "",
        phoneNumber: "",
        userGender: "",
        genderPreference: "",
        subscriptionPlan: "",
        lookingFor: "",
        height: "",
        status: "",
        onlineStatus: false,
        notificationStatus: false,
        aboutMe: "",
        motherTongue: "",
        religion: "",
        community: "",
        degree: "",
        dayJob: "",
        growUpCountry: "",
        originCountry: "",
        settledCountry: "",
        bodyBuild: "",
        profileImageUrls: [],
        fcmToken: "",
        profileDiscovery: false,
        referrerUserId: "",
        referrals: [],
        currentDiscountPercentage: 0,
        blockedUsers: [],
        isFirstTimeSubscription: false,
        currentPosition: {
          address: "",
          city: "",
          latitude: "",
          longitude: "",
          countryCode: ""
        },
        password: "",
        confirmPassword: "",
        selectedLikesInvolvesMap: {
          books: [],
          childrenView: [],
          foods: [],
          hobbies: [],
          interests: [],
          movies: [],
          music: [],
          relaxWay: [],
          sleepingHabit: [],
          sports: [],
          tvShows: [],
          vacations: [],
        },
        selectedPersonalityTraitsMap: {
          drink: "",
          eveningRoutine: [],
          exercise: "",
          passions: [],
          personalityType: "",
          pets: [],
          poison: [],
          smoke: "",
          starSign: "",
          tripsType: [],
          weatherType: [],
          weekendActivities: [],
          weekendNight: []
        }
      });
      setImageFiles([]);

      // Refresh user list
      const usersCollection = collection(db, "users");
      const userSnapshot = await getDocs(usersCollection);
      const userList = userSnapshot.docs
        .filter((doc) => doc.id !== 'RegisteredUsers')
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      setUsers(userList);
      setFilteredUsers(userList);

      setSearchNameTerm("");
      setSearchIdTerm("");

      navigate('/manage-users');
    } catch (error) {
      console.error("Error creating user or storing data: ", error);
      showError(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFormVisibility = () => {
    setIsFormVisible(true);
  };

  const handleView = (newUserId) => {
    navigate(`/user-details/${newUserId}`);
  };

  const handleEdit = (newUserId) => {
    if (userRole === 0) {
      showError("You don't have permission to edit users.");
      return;
    }
    navigate(`/edit-user/${newUserId}`);
  };

  const handleDelete = async (newUserId) => {
    const confirmation = window.confirm("Are you sure you want to delete this user? This action cannot be undone.");

    if (confirmation) {
      try {
        setIsLoading(true);
        const userRef = doc(db, "users", newUserId);
        await deleteDoc(userRef);

        const updatedUsers = users.filter((user) => user.id !== newUserId);
        setUsers(updatedUsers);

        let filtered = updatedUsers;
        if (searchNameTerm) {
          filtered = filtered.filter(user =>
            user.name && user.name.toLowerCase().includes(searchNameTerm.toLowerCase())
          );
        }
        if (searchIdTerm) {
          filtered = filtered.filter(user =>
            (user.id && user.id.toLowerCase().includes(searchIdTerm.toLowerCase())) ||
            (user.userId && user.userId.toLowerCase().includes(searchIdTerm.toLowerCase()))
          );
        }
        setFilteredUsers(filtered);
        setIsLoading(false);

        showSuccess("User deleted successfully!");
      } catch (error) {
        console.error("Error deleting user: ", error);
        showError("There was an error deleting the user!");
        setIsLoading(false);
      }
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "User ID",
        accessor: row => row.userId || row.id,
        id: "userId",
      },
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Date of Birth",
        accessor: "dateOfBirth",
      },
      {
        Header: "Phone Number",
        accessor: "phoneNumber",
      },
      {
        Header: "Action",
        id: "action",
        Cell: ({ row }) => (
          <div className="action-buttons">
            <button onClick={() => handleView(row.original.id)} title="View" className="action-btn view-btn">
              <i className="fas fa-eye"></i>
            </button>
            <button
              onClick={() => handleEdit(row.original.id)}
              title="Edit"
              disabled={userRole === 0}
              className={`action-btn edit-btn ${userRole === 0 ? 'disabled' : ''}`}
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={() => handleDelete(row.original.id)}
              title="Delete"
              className="action-btn delete-btn"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [userRole]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredUsers,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useSortBy,
    usePagination
  );

  const loaderStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100px",
    width: "100%"
  };

  const spinnerStyle = {
    width: "40px",
    height: "40px",
    border: "4px solid rgba(0, 0, 0, 0.1)",
    borderLeft: "4px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  };

  const loaderKeyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  if (isCheckingRole) {
    return (
      <div className="manage-users">
        <style>{loaderKeyframes}</style>
        <div style={loaderStyle}>
          <div style={spinnerStyle}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-users">
      <style>{loaderKeyframes}</style>
      
      {successMessage && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage("")} className="alert-close">×</button>
        </div>
      )}
      
      {errorMessage && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage("")} className="alert-close">×</button>
        </div>
      )}

      <div className="page-header">
        <h2>Manage Users</h2>
        <button onClick={toggleFormVisibility} className="add-user-btn">
          <i className="fas fa-user-plus"></i>
          Add New User
        </button>
      </div>

      {!isFormVisible && (
        <div className="filters-container">
          <div className="search-grid">
            <div className="search-box">
              <label htmlFor="search-name-input" className="search-label">
                <i className="fas fa-user"></i> Name
              </label>
              <input
                id="search-name-input"
                type="text"
                placeholder="Search by name..."
                value={searchNameTerm}
                onChange={handleNameSearchChange}
                className="search-input"
              />
            </div>
            <div className="search-box">
              <label htmlFor="search-id-input" className="search-label">
                <i className="fas fa-id-card"></i> User ID
              </label>
              <input
                id="search-id-input"
                type="text"
                placeholder="Search by user ID..."
                value={searchIdTerm}
                onChange={handleIdSearchChange}
                className="search-input"
              />
            </div>
            <div className="search-box">
              <label htmlFor="search-plan-input" className="search-label">
                <i className="fas fa-crown"></i> Subscription Plan
              </label>
              <select
                id="search-plan-input"
                value={searchPlanTerm}
                onChange={handlePlanChange}
                className="search-input"
              >
                <option value="">All Plans</option>
                <option value="Free">Free</option>
                <option value="Ybe Plus">Ybe Plus</option>
                <option value="Ybe Premium">Ybe Premium</option>
                <option value="Ybe Gold">Ybe Gold</option>
              </select>
            </div>
            <div className="search-box">
              <label htmlFor="search-date-input" className="search-label">
                <i className="fas fa-calendar"></i> Date of Joining
              </label>
              <input
                id="search-date-input"
                type="date"
                value={searchDateTerm}
                onChange={(e) => setSearchDateTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>
      )}

      {isFormVisible && (
        <div className="form-wrapper">
          <div className="form-header">
            <h3>Add New User</h3>
            <button onClick={() => setIsFormVisible(false)} className="close-form-btn">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={userDetails.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={userDetails.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  name="location"
                  value={userDetails.location}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Country *</label>
                <input
                  type="text"
                  name="country"
                  value={userDetails.country}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={userDetails.dateOfBirth}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={userDetails.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={userDetails.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Gender</label>
                <select
                  name="userGender"
                  value={userDetails.userGender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={userDetails.phoneNumber}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Profile Images (3-6 images) *</label>
              <div className="file-input-wrapper">
              <input
              type="file"
              name="profileImages"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleImageChange}
              id="file-input"
            />
                <label htmlFor="file-input" className="file-input-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  Choose Images
                </label>
                <span className="file-count">
                  {imageFiles.length} file(s) selected
                </span>
              </div>
            </div>

            <div className="image-preview-container">
              {userDetails.imagePreviews && userDetails.imagePreviews.map((preview, index) => (
                <div key={index} className="image-preview">
                  <button 
                    type="button"
                    className="remove-image-btn" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(index);
                    }}
                    title="Remove image"
                  >
                    ×
                  </button>
                  <div onClick={() => handleImageClick(index)}>
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <div className="image-overlay">
                      <i className="fas fa-search-plus"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedImageIndex !== null && (
              <div className="image-modal" onClick={closeModal}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <span className="close-button" onClick={closeModal}>&times;</span>
                  <img src={userDetails.imagePreviews[selectedImageIndex]} alt="Full size preview" />
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Adding User...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus"></i>
                    Add User
                  </>
                )}
              </button>
              <button type="button" onClick={() => setIsFormVisible(false)} className="cancel-btn">
                <i className="fas fa-times"></i>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {!isFormVisible && (
        <div className="table-wrapper">
          <div className="table-container">
            <table {...getTableProps()} className="user-table">
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        <div className="th-content">
                          {column.render('Header')}
                          <span className="sort-icon">
                            {column.isSorted
                              ? column.isSortedDesc
                                ? ' 🔽'
                                : ' 🔼'
                              : ''}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {isLoading ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: 'center', padding: '20px' }}>
                      <div style={loaderStyle}>
                        <div style={spinnerStyle}></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="no-results-message">
                      <i className="fas fa-search"></i>
                      <p>No data found for the search criteria</p>
                    </td>
                  </tr>
                ) : (
                  page.map(row => {
                    prepareRow(row);
                    return (
                      <tr key={row.id} {...row.getRowProps()}>
                        {row.cells.map(cell => (
                          <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <div className="pagination-info">
              Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, filteredUsers.length)} of {filteredUsers.length} entries
            </div>
            <div className="pagination-controls">
              <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="pagination-btn">
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button onClick={() => previousPage()} disabled={!canPreviousPage} className="pagination-btn">
                <i className="fas fa-angle-left"></i>
              </button>
              <span className="page-info">
                Page {pageIndex + 1} of {pageOptions.length}
              </span>
              <button onClick={() => nextPage()} disabled={!canNextPage} className="pagination-btn">
                <i className="fas fa-angle-right"></i>
              </button>
              <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="pagination-btn">
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
            <div className="page-size-selector">
              <label>Show</label>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
              >
                {[10, 20, 30, 50].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <label>entries</label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;